/**
 * The one-time "you are editing a running plan" note, and the flag that retires it.
 *
 * Mobile's `lib/liveNoteStorage.ts`, with its asynchronous read collapsed: `localStorage` answers
 * synchronously, so there is no resolving state and no flash to gate. The note shows on a running
 * fork until the trainer dismisses it once, on this browser.
 *
 * **Per viewer, and deliberately not per plan.** The sentence is about what editing a live plan
 * does at all, not about one client's plan, so a trainer who has read it once has read it.
 *
 * Every access is wrapped: reading storage can THROW rather than answer `null` (a private window, a
 * blocked origin), and a note is never worth taking the builder down for. A failed read shows the
 * note again, which is the harmless half of being wrong.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { PreferenceStore } from '~/shared/lib/preferenceStore';

const KEY = 'movebuddy.planBuilder.liveNoteSeen';

export function hasSeenLiveNote(store: PreferenceStore | null): boolean {
  if (store === null) return false;
  try {
    return store.getItem(KEY) === 'true';
  } catch {
    return false;
  }
}

export function markLiveNoteSeen(store: PreferenceStore | null): void {
  if (store === null) return;
  try {
    store.setItem(KEY, 'true');
  } catch {
    // A full or refused storage means the note is offered once more. Nothing else depends on it.
  }
}
